import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  History, 
  CheckCircle2, 
  Flame,
  Calendar,
  Trophy,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Zap,
  Award
} from "lucide-react";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Month names in Spanish
const monthNames = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const dayNames = ["L", "M", "X", "J", "V", "S", "D"];

// Mini Calendar Component
const MiniCalendar = ({ history }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Get completed dates as Set for quick lookup
  const completedDates = useMemo(() => {
    const dates = new Set();
    history?.forEach(workout => {
      if (workout.completed_at) {
        const date = new Date(workout.completed_at);
        dates.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
      }
    });
    return dates;
  }, [history]);

  // Get calendar data for current month
  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;
    
    const days = [];
    
    // Add empty cells for days before the first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, completed: false, isToday: false, isFuture: false });
    }
    
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
    
    // Add days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = `${year}-${month}-${d}`;
      const isToday = isCurrentMonth && today.getDate() === d;
      const isFuture = new Date(year, month, d) > today;
      
      days.push({
        day: d,
        completed: completedDates.has(dateKey),
        isToday,
        isFuture
      });
    }
    
    return days;
  }, [currentMonth, completedDates]);

  // Count completed workouts this month
  const monthlyStats = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    let count = 0;
    
    history?.forEach(workout => {
      if (workout.completed_at) {
        const date = new Date(workout.completed_at);
        if (date.getFullYear() === year && date.getMonth() === month) {
          count++;
        }
      }
    });
    
    return count;
  }, [currentMonth, history]);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (next <= new Date()) {
      setCurrentMonth(next);
    }
  };

  const canGoNext = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) <= new Date();

  return (
    <Card className="bg-[#121212] border-[#2A2A2A]">
      <CardContent className="p-4">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={prevMonth}
            className="p-2 rounded-lg hover:bg-[#1A1A1A] text-[#888] hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <h3 className="text-white font-heading font-bold">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <p className="text-xs text-[#D4AF37]">
              {monthlyStats} {monthlyStats === 1 ? "entreno" : "entrenos"} este mes
            </p>
          </div>
          <button
            onClick={nextMonth}
            disabled={!canGoNext}
            className={cn(
              "p-2 rounded-lg transition-colors",
              canGoNext 
                ? "hover:bg-[#1A1A1A] text-[#888] hover:text-white" 
                : "text-[#333] cursor-not-allowed"
            )}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day, i) => (
            <div key={i} className="text-center text-xs text-[#555] font-medium py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarData.map((item, index) => (
            <div
              key={item.day ? `day-${item.day}` : `empty-${index}`}
              className={cn(
                "aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all",
                !item.day && "invisible",
                item.day && item.completed && "bg-[#22C55E] text-white shadow-md shadow-[#22C55E]/30",
                item.day && !item.completed && item.isToday && "bg-[#D4AF37]/20 border-2 border-[#D4AF37] text-[#D4AF37]",
                item.day && !item.completed && !item.isToday && !item.isFuture && "bg-[#1A1A1A] text-[#666]",
                item.day && item.isFuture && "text-[#333]"
              )}
            >
              {item.day}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-[#2A2A2A]">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-[#22C55E]" />
            <span className="text-xs text-[#666]">Completado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border-2 border-[#D4AF37]" />
            <span className="text-xs text-[#666]">Hoy</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const HistorialPage = () => {
  const { getAuthHeaders } = useAuth();
  const [historyData, setHistoryData] = useState(null);
  const [progressSummary, setProgressSummary] = useState(null);
  const [personalRecords, setPersonalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("historial"); // historial, records

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [historyRes, summaryRes, prsRes] = await Promise.all([
        axios.get(`${API_URL}/api/workout/history`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/tracking/progress-summary`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/tracking/all-prs`, { headers: getAuthHeaders() }).catch(() => ({ data: { personal_records: [] } }))
      ]);
      
      setHistoryData(historyRes.data);
      setProgressSummary(summaryRes.data);
      setPersonalRecords(prsRes.data.personal_records || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="h-32 bg-[#121212] animate-pulse rounded-2xl" />
          <div className="h-32 bg-[#121212] animate-pulse rounded-2xl" />
        </div>
        <div className="h-64 bg-[#121212] animate-pulse rounded-2xl" />
        <div className="h-28 bg-[#121212] animate-pulse rounded-2xl" />
      </div>
    );
  }

  const { history, streak, best_streak, total } = historyData || { history: [], streak: 0, best_streak: 0, total: 0 };

  const formatVolume = (volume) => {
    if (!volume) return "0";
    if (volume >= 1000) {
      return `${(volume / 1000).toFixed(1)}k`;
    }
    return Math.round(volume).toString();
  };

  const formatPRDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short"
    });
  };

  return (
    <div className="space-y-5 animate-fade-in pb-6" data-testid="historial-page">
      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: "historial", label: "Historial", icon: History },
          { id: "records", label: "Récords", icon: Trophy }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-colors",
              activeTab === tab.id
                ? "bg-[#D4AF37] text-[#0B0B0B]"
                : "bg-[#1A1A1A] text-[#888] hover:text-white"
            )}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* HISTORIAL TAB */}
      {activeTab === "historial" && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3">
        {/* Streak Card */}
        <Card className={cn(
          "border",
          streak > 0 
            ? "bg-gradient-to-br from-orange-500/10 to-red-500/5 border-orange-500/30" 
            : "bg-[#121212] border-[#2A2A2A]"
        )}>
          <CardContent className="p-4 text-center">
            <div className={cn(
              "w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2",
              streak > 0 ? "bg-gradient-to-br from-orange-500 to-red-500" : "bg-[#1A1A1A]"
            )}>
              <Flame className={cn(
                "w-6 h-6",
                streak > 0 ? "text-white" : "text-[#555]"
              )} />
            </div>
            <p className={cn(
              "text-4xl font-heading font-black",
              streak > 0 ? "text-white" : "text-[#555]"
            )}>
              {streak}
            </p>
            <p className="text-xs text-[#666] mt-0.5">Racha actual</p>
            {best_streak > 0 && (
              <p className="text-xs text-[#555] mt-1 flex items-center justify-center gap-1">
                <Trophy className="w-3 h-3 text-[#D4AF37]" />
                Mejor: {best_streak}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Total Card */}
        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 mx-auto rounded-xl bg-[#22C55E]/20 flex items-center justify-center mb-2">
              <Trophy className="w-6 h-6 text-[#22C55E]" />
            </div>
            <p className="text-4xl font-heading font-black text-[#22C55E]">{total}</p>
            <p className="text-xs text-[#666] mt-0.5">Entrenamientos</p>
            {total > 0 && (
              <p className="text-xs text-[#555] mt-1">
                {total >= 24 ? "¡Programa completado!" : `${24 - total} para completar`}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Calendar View */}
      <MiniCalendar history={history} />

      {/* Progress indicator */}
      {total > 0 && streak > 0 && (
        <Card className="bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border-[#D4AF37]/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">
                  {streak >= 7 ? "¡Modo GOAT activado!" : 
                   streak >= 3 ? "¡En racha! No pares." : 
                   "¡Buen inicio!"}
                </p>
                <p className="text-xs text-[#888]">
                  La constancia es tu superpoder
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History List */}
      <div>
        <h2 className="font-heading text-lg text-white font-bold mb-3 flex items-center gap-2">
          <History className="w-5 h-5 text-[#D4AF37]" />
          Historial reciente
        </h2>

        {history.length === 0 ? (
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-xl bg-[#1A1A1A] flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-[#444]" />
              </div>
              <h3 className="text-base font-heading font-bold text-white mb-1">Sin historial</h3>
              <p className="text-[#666] text-sm">
                Completa tu primer entrenamiento.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[calc(100dvh-680px)] min-h-[200px]">
            <div className="space-y-2 pb-4">
              {history.slice(0, 10).map((workout, index) => (
                <Card
                  key={workout.id || index}
                  className="bg-[#121212] border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors"
                  data-testid={`history-item-${index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-lg bg-[#22C55E]/15 border border-[#22C55E]/30 flex items-center justify-center">
                          <span className="text-lg font-heading font-black text-[#22C55E]">
                            {workout.workout_code || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="font-bold text-white text-sm">
                            {workout.workout_name || `Día ${workout.workout_code}`}
                          </p>
                          <p className="text-xs text-[#666]">
                            {formatDate(workout.completed_at)} • {formatTime(workout.completed_at)}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30 text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {workout.exercises_completed?.length || 0}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
        </>
      )}

      {/* RECORDS TAB */}
      {activeTab === "records" && (
        <div className="space-y-4">
          {/* Volume Stats */}
          {progressSummary && (
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30">
                <CardContent className="p-4 text-center">
                  <Zap className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-black text-white">{formatVolume(progressSummary.total_volume_kg)}</p>
                  <p className="text-[#888] text-xs">Kg totales movidos</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30">
                <CardContent className="p-4 text-center">
                  <Trophy className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-black text-white">{progressSummary.total_personal_records || 0}</p>
                  <p className="text-[#888] text-xs">Récords personales</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Strongest Exercise */}
          {progressSummary?.strongest_exercise?.name && (
            <Card className="bg-[#121212] border-[#2A2A2A]">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[#666] text-xs uppercase tracking-wider">Tu ejercicio más fuerte</p>
                    <p className="text-white font-bold">{progressSummary.strongest_exercise.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#D4AF37] text-2xl font-black">{progressSummary.strongest_exercise.weight}</p>
                    <p className="text-[#666] text-xs">kg</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent PRs Badge */}
          {progressSummary?.recent_prs > 0 && (
            <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold">¡{progressSummary.recent_prs} nuevos récords esta semana!</p>
                    <p className="text-green-400 text-sm">Sigue así, estás progresando</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* PRs List */}
          <div>
            <h2 className="font-heading text-lg text-white font-bold mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#D4AF37]" />
              Récords personales
            </h2>

            {personalRecords.length === 0 ? (
              <Card className="bg-[#121212] border-[#2A2A2A]">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto rounded-xl bg-[#1A1A1A] flex items-center justify-center mb-4">
                    <Dumbbell className="w-8 h-8 text-[#444]" />
                  </div>
                  <h3 className="text-base font-heading font-bold text-white mb-1">Sin récords aún</h3>
                  <p className="text-[#666] text-sm">
                    Registra peso en tus ejercicios para crear tus PRs
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {personalRecords.slice(0, 15).map((pr, idx) => (
                  <Card key={pr.exercise_id || idx} className="bg-[#121212] border-[#2A2A2A]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center flex-shrink-0">
                          <Trophy className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{pr.exercise_name}</p>
                          <p className="text-[#666] text-xs mt-0.5">
                            {formatPRDate(pr.updated_at)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[#D4AF37] text-xl font-bold">{pr.max_weight}<span className="text-sm text-[#888]">kg</span></p>
                          {pr.best_volume > 0 && (
                            <p className="text-[#555] text-xs">{formatVolume(pr.best_volume)} vol.</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistorialPage;
